import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { StarRating, StarDisplay } from "@/components/StarRating";
import { Ambulance, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RiderRating() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | ready | already | done | error
  const [ride, setRide] = useState(null);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    axios.get(`${API}/ratings/lookup/${token}`)
      .then((r) => {
        setRide(r.data);
        if (r.data.already_submitted) setState("already");
        else setState("ready");
      })
      .catch((e) => {
        setErrMsg(e.response?.data?.detail || "This rating link is invalid or expired.");
        setState("error");
      });
  }, [token]);

  const submit = async () => {
    if (stars < 1) return toast.error("Please select a star rating");
    setSubmitting(true);
    try {
      await axios.post(`${API}/ratings/submit/${token}`, { stars, comment });
      setState("done");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit rating");
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="rider-rating-page">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-lg mx-auto px-5 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-[#005E82] flex items-center justify-center"><Ambulance size={18} /></div>
          <div>
            <div className="font-display font-semibold text-sm">
              {ride?.agency_name || "Ride feedback"}
            </div>
            <div className="text-[11px] text-slate-400">How was your trip?</div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-lg mx-auto w-full px-4 py-6">
        {state === "loading" && (
          <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-sm text-slate-500">Loading…</div>
        )}

        {state === "error" && (
          <div className="bg-white border border-slate-200 rounded-md p-8 text-center" data-testid="rating-error">
            <div className="text-rose-600 font-display font-semibold mb-1">Link unavailable</div>
            <div className="text-sm text-slate-600">{errMsg}</div>
          </div>
        )}

        {state === "already" && (
          <div className="bg-white border border-slate-200 rounded-md p-8 text-center" data-testid="rating-already">
            <CheckCircle2 size={32} className="text-emerald-600 mx-auto mb-2" />
            <div className="font-display font-semibold text-slate-900 mb-1">You've already rated this trip</div>
            <div className="text-sm text-slate-500">Thank you for your feedback.</div>
          </div>
        )}

        {state === "done" && (
          <div className="bg-white border border-slate-200 rounded-md p-8 text-center fade-in" data-testid="rating-done">
            <CheckCircle2 size={40} className="text-emerald-600 mx-auto mb-3" />
            <div className="font-display font-semibold text-slate-900 text-lg mb-1">Thanks for rating your trip</div>
            <div className="text-sm text-slate-600">Your feedback helps keep the service safe and reliable.</div>
          </div>
        )}

        {state === "ready" && ride && (
          <div className="space-y-4 fade-in">
            <div className="bg-white border border-slate-200 rounded-md p-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Trip</div>
              <div className="font-display font-semibold text-slate-900 mt-0.5">
                {ride.patient_first_name ? `Ride for ${ride.patient_first_name}` : "Your ride"}
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-1">
                <Clock size={12} /> {ride.pickup_date}
              </div>
              {ride.driver_name && (
                <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
                  Driver: <span className="text-slate-800 font-medium">{ride.driver_name}</span>
                </div>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-5">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Your rating</div>
              <div className="flex justify-center py-2">
                <StarRating value={stars} onChange={setStars} size={36} testId="rider-stars" />
              </div>
              <div className="text-center text-xs text-slate-500 mb-4">
                {stars === 0 && "Tap a star"}
                {stars === 1 && "Not good"}
                {stars === 2 && "Below average"}
                {stars === 3 && "Okay"}
                {stars === 4 && "Great"}
                {stars === 5 && "Excellent"}
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Comments (optional)</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  maxLength={1000}
                  placeholder="Anything you'd like to share about your ride?"
                  className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  data-testid="rider-comment"
                />
              </div>
              <button
                onClick={submit}
                disabled={submitting || stars < 1}
                className="mt-4 w-full bg-[#005E82] hover:bg-[#004a68] disabled:opacity-50 text-white text-sm rounded-md py-2.5 font-semibold"
                data-testid="rider-submit"
              >
                {submitting ? "Submitting…" : "Submit rating"}
              </button>
              <div className="text-[10px] text-slate-400 text-center mt-3">
                Your driver will only see your rating after you both submit.
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="text-center text-[11px] text-slate-400 py-4">RideWise NEMT · secure feedback</footer>
    </div>
  );
}
